/*
  ProgramBlockDialogs — three dialogs used to reuse exercise-program
  content across clients:

    * SaveAsBlockDialog — save a section (or a subset) as a reusable
      block. Strips client identifiers server-side; surfaces any
      free-text identifier warnings so the operator can review.
    * InsertBlockDialog — search + preview the library and snapshot
      a chosen block into the current program.
    * BlockLibraryPage — full library management view (search,
      filter, publish, archive).

  All three call the /program-blocks/* backend introduced 2026-03-02.
  Blocks are always inserted as SNAPSHOTS so future library edits
  never silently change any client program.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Search, X, Layers, Send, Archive, RotateCcw, Trash2,
  Sparkles, Info, ShieldCheck, CheckCircle2, Star,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";


// ---------------------------------------------------------------------------
// SaveAsBlockDialog
// ---------------------------------------------------------------------------

export function SaveAsBlockDialog({
  open, onOpenChange, sourceProgramId, sectionIndex, section, onSaved,
  // When set, the dialog saves via POST /program-blocks/from-section-content
  // (works for template sections that aren't part of a live program).
  sectionContent = null,
  sourceKind = null,
}) {
  const [name, setName] = useState("");
  const [service, setService] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);
  const [pickedIds, setPickedIds] = useState(null); // null = whole section

  useEffect(() => {
    if (open) {
      // Seed name from the section label so a save is 2 clicks in
      // the common case ("save this whole section, keep its name").
      setName((section?.label || "").trim());
      setService(""); setDescription(""); setTags("");
      setPickedIds(null); setSaving(false);
    }
  }, [open, section?.label]);

  const exs = section?.exercises || [];
  const allPicked = !pickedIds || pickedIds.length === exs.length;

  function toggleExercise(id) {
    setPickedIds((cur) => {
      const set = new Set(cur ?? exs.map((e) => e.id));
      if (set.has(id)) set.delete(id); else set.add(id);
      return Array.from(set);
    });
  }

  async function submit() {
    if (!name.trim()) {
      toast.error("Block needs a name");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim(),
        service: service.trim(),
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      let endpoint;
      if (sectionContent) {
        // Template / drafter path — supply the raw section content.
        // Optional per-exercise picking still works: pass filtered
        // exercises through directly.
        const filtered = allPicked
          ? sectionContent
          : { ...sectionContent, exercises: exs.filter((e) => (pickedIds || []).includes(e.id)) };
        body.section = filtered;
        body.source_kind = sourceKind || "template";
        endpoint = "/program-blocks/from-section-content";
      } else {
        body.source_program_id = sourceProgramId;
        body.section_index = sectionIndex;
        if (!allPicked) body.exercise_ids = pickedIds;
        endpoint = "/program-blocks/from-selection";
      }
      const r = await api.post(endpoint, body);
      // Surface any free-text warnings so the operator can review.
      const warns = r.data?.version?.warnings || [];
      if (warns.length > 0) {
        toast.warning("Block saved — please review flagged text", {
          description: warns.join(" · "),
        });
      } else {
        toast.success("Block saved and published");
      }
      onOpenChange(false);
      if (onSaved) onSaved(r.data);
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="save-as-block-dialog">
        <DialogHeader>
          <DialogTitle>Save section as reusable block</DialogTitle>
          <DialogDescription>
            Client-specific state (daily logs, pain, session notes)
            is stripped automatically. The block is published straight
            away so you can reuse it across clients.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-2xs uppercase tracking-wide text-zinc-500">
              Block name
            </label>
            <Input
              autoFocus value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. PD amplitude warm-up (standing)"
              className="mt-0.5"
              data-testid="save-block-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-2xs uppercase tracking-wide text-zinc-500">Service</label>
              <Input
                value={service}
                onChange={(e) => setService(e.target.value)}
                placeholder="e.g. onero, parkinsons"
                className="mt-0.5"
                data-testid="save-block-service"
              />
            </div>
            <div>
              <label className="text-2xs uppercase tracking-wide text-zinc-500">Tags (comma)</label>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="balance, seated"
                className="mt-0.5"
                data-testid="save-block-tags"
              />
            </div>
          </div>
          <div>
            <label className="text-2xs uppercase tracking-wide text-zinc-500">Description (optional)</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Short note for other practitioners"
              className="mt-0.5"
              data-testid="save-block-description"
            />
          </div>
          <div className="border rounded p-2 max-h-48 overflow-y-auto"
                data-testid="save-block-exercise-picker">
            <div className="text-2xs uppercase tracking-wide text-zinc-500 mb-1">
              Exercises to include ({(pickedIds ?? exs.map((e) => e.id)).length}/{exs.length})
            </div>
            {exs.length === 0 ? (
              <div className="text-xs text-zinc-500 py-2 text-center">
                This section has no exercises yet.
              </div>
            ) : exs.map((ex) => {
              const isPicked = !pickedIds || pickedIds.includes(ex.id);
              return (
                <label key={ex.id}
                        className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPicked}
                    onChange={() => toggleExercise(ex.id)}
                    data-testid={`save-block-ex-${ex.id}`}
                  />
                  <span className="truncate flex-1">{ex.name || "—"}</span>
                  <span className="text-2xs text-zinc-500">
                    {[ex.sets, ex.reps].filter(Boolean).join(" × ")}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  disabled={saving} data-testid="save-block-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || exs.length === 0}
                  data-testid="save-block-submit">
            {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</>
                    : <>Save block</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// InsertBlockDialog
// ---------------------------------------------------------------------------

export function InsertBlockDialog({
  open, onOpenChange, programId, sections, onInserted,
}) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [inserting, setInserting] = useState(false);
  const [position, setPosition] = useState("end");
  const [anchor, setAnchor] = useState(0);
  // Favourites-only filter — off by default so a first-time
  // user still sees the whole (published) library.
  const [favOnly, setFavOnly] = useState(false);
  const seq = useRef(0);

  const search = useCallback(async (needle, favouritesOnly) => {
    const my = ++seq.current;
    setLoading(true);
    try {
      const params = { q: needle, status: "published", limit: 30 };
      if (favouritesOnly) params.favourites_only = true;
      const r = await api.get("/program-blocks", { params });
      if (my !== seq.current) return;
      setItems(r.data?.blocks || []);
    } catch (e) {
      if (my === seq.current) {
        toast.error("Search failed", { description: e?.response?.data?.detail || e.message });
      }
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setQ(""); setItems([]); setPicked(null);
      setPosition("end"); setAnchor(0); setFavOnly(false);
      search("", false);
    }
  }, [open, search]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(q, favOnly), 250);
    return () => clearTimeout(t);
  }, [q, favOnly, open, search]);

  async function toggleFavourite(block, e) {
    if (e) e.stopPropagation();
    const next = !block.is_favourite;
    // Optimistic update — the picker feels instant.
    setItems((rows) => rows.map((r) => r.id === block.id
      ? { ...r, is_favourite: next } : r));
    if (picked?.id === block.id) {
      setPicked({ ...picked, is_favourite: next });
    }
    try {
      await api.post(`/program-blocks/${block.id}/favourite`,
                      { favourite: next });
    } catch (err) {
      // Roll back on failure.
      setItems((rows) => rows.map((r) => r.id === block.id
        ? { ...r, is_favourite: !next } : r));
      toast.error("Couldn't update favourite", {
        description: err?.response?.data?.detail || err.message,
      });
    }
  }

  async function submit() {
    if (!picked) return;
    setInserting(true);
    try {
      const body = { block_id: picked.id, position };
      if (position !== "end") body.anchor_section_index = anchor;
      await api.post(`/exercise-programs/${programId}/insert-block`, body);
      toast.success(`Inserted "${picked.name}"`);
      onOpenChange(false);
      if (onInserted) onInserted();
    } catch (e) {
      toast.error("Insert failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setInserting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="insert-block-dialog">
        <DialogHeader>
          <DialogTitle>Insert reusable block</DialogTitle>
          <DialogDescription>
            Blocks are snapshot into the program — future library
            edits won't change this client.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="relative mb-2">
              <Search className="h-3.5 w-3.5 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <Input
                autoFocus value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search block name, tag, service…"
                className="h-8 pl-7 text-xs"
                data-testid="insert-block-search"
              />
            </div>
            <label className="flex items-center gap-1.5 text-2xs text-zinc-600 mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={favOnly}
                onChange={(e) => setFavOnly(e.target.checked)}
                data-testid="insert-block-fav-only"
              />
              <Star className={`h-3 w-3 ${favOnly ? "fill-amber-400 text-amber-500" : "text-zinc-400"}`} />
              My favourites only
            </label>
            <div className="border rounded max-h-80 overflow-y-auto divide-y divide-zinc-100">
              {loading ? (
                <div className="text-xs text-zinc-500 py-4 text-center">
                  <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> Loading…
                </div>
              ) : items.length === 0 ? (
                <div className="text-xs text-zinc-500 py-6 text-center"
                      data-testid="insert-block-empty">
                  {favOnly
                    ? "No favourites yet — star a block in the library or below."
                    : q.trim()
                      ? <>No published blocks match “<b>{q.trim()}</b>”.</>
                      : "No published blocks yet — save one from a program section."}
                </div>
              ) : items.map((b) => (
                <div
                  key={b.id}
                  className={`px-3 py-2 flex items-start gap-1.5 cursor-pointer ${
                    picked?.id === b.id ? "bg-indigo-50" : "hover:bg-zinc-50"
                  }`}
                  onClick={() => setPicked(b)}
                  data-testid={`insert-block-item-${b.id}`}
                >
                  <button
                    type="button"
                    onClick={(e) => toggleFavourite(b, e)}
                    className="mt-0.5 p-0.5 rounded hover:bg-zinc-100 shrink-0"
                    aria-label={b.is_favourite ? "Un-favourite" : "Favourite"}
                    title={b.is_favourite ? "Remove from favourites" : "Add to favourites"}
                    data-testid={`insert-block-fav-${b.id}`}
                  >
                    <Star className={`h-3.5 w-3.5 ${
                      b.is_favourite ? "fill-amber-400 text-amber-500" : "text-zinc-400"
                    }`} />
                  </button>
                  <Layers className="h-3.5 w-3.5 text-zinc-500 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="text-sm font-medium truncate flex-1">{b.name}</div>
                      <Badge variant="outline" className="text-2xs">
                        {b.content_summary?.exercise_count ?? 0} exs
                      </Badge>
                    </div>
                    <div className="text-2xs text-zinc-500 truncate mt-0.5">
                      {[b.service, ...(b.tags || [])].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-2xs uppercase tracking-wide text-zinc-500 mb-1">
              Preview
            </div>
            {picked ? (
              <div className="border rounded p-2 text-sm">
                <div className="font-medium mb-1">{picked.name}</div>
                {picked.description && (
                  <div className="text-xs text-zinc-600 mb-2">{picked.description}</div>
                )}
                <ul className="text-xs list-disc pl-4 space-y-0.5"
                    data-testid="insert-block-preview-exercises">
                  {(picked.content_summary?.exercise_names || []).map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
                <div className="mt-3 space-y-1">
                  <label className="text-2xs uppercase tracking-wide text-zinc-500">
                    Insert
                  </label>
                  <select
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    className="w-full h-8 text-xs border border-zinc-200 rounded px-1 bg-white"
                    data-testid="insert-block-position"
                  >
                    <option value="end">at the end (as new section)</option>
                    <option value="before">before selected section</option>
                    <option value="after">after selected section</option>
                    <option value="append_to">into selected section</option>
                  </select>
                  {position !== "end" && (
                    <select
                      value={anchor}
                      onChange={(e) => setAnchor(parseInt(e.target.value, 10))}
                      className="w-full h-8 text-xs border border-zinc-200 rounded px-1 bg-white"
                      data-testid="insert-block-anchor"
                    >
                      {(sections || []).map((s, i) => (
                        <option key={i} value={i}>{i + 1}. {s.label || s.key || "(section)"}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-xs text-zinc-500 py-8 text-center border rounded">
                Pick a block to preview
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  disabled={inserting} data-testid="insert-block-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!picked || inserting}
                  data-testid="insert-block-submit">
            {inserting
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Inserting…</>
              : <>Insert block</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
